/**
 * Precompiled view components (plans/15 in aeppic-v4): the browser side.
 *
 * Layouts, designs, controls and lists are compiled on the server, one ES
 * module and stylesheet per component group. A component's group follows from
 * its document's ancestry (`componentGroup` with the rules of the
 * `view-components` feature, the same rules the server uses), its URL from the
 * group's current key, which the server sends with the feature
 * (`groupKeys`).
 *
 * `ensure(componentId, document)` registers a component from its group:
 * the group is loaded once per page, its module registers the component
 * (`ensureComponent`), and the registration's `load(…)` imports the
 * component's packages and builds its options, which are registered in Vue.
 * The group's stylesheet holds its controls, designs and lists. A layout
 * brings its own stylesheet with its registration, added when the layout is
 * first used and kept for the rest of the page's life, as for a layout built
 * here (DynamicComponent): layouts often carry global rules, which must not
 * apply just because their group was loaded.
 * It answers false when the component is not in its group (a newer version,
 * a component that did not compile) or the group cannot be loaded: the
 * caller builds the component in the browser, as without this feature.
 *
 * Every group loaded is recorded, from the start of the page load, in the
 * format of the `preload` option (`preloadList()`), to be copied into
 * `server.json`.
 */
import * as Types from '@aeppic/types'

import { componentGroup, groupUrls, type GroupRule } from '@aeppic/dynamic-components-v3'

import Vue from '../../externals/vue.js'
import * as FieldParams from '../field-params.js'
import { perf } from '../perf.js'
import { loadStyle, loadStyleFromUrl } from '../utils/css'

export const USED_GROUPS_STORAGE_KEY = 'aeppic:view-components:used'

export interface ViewComponentsFeatureOptions {
  groups?: GroupRule[]
  preload?: string[]
  /** Set by the server when its compiler works. */
  buildKey?: string
  /**
   * The current key of every group, from the server: group URLs are built
   * from them (the browser's own documents compute DTS differently, and
   * reading documents during boot may wait for the local model).
   */
  groupKeys?: Record<string, string>
  /** Present only when the server enables learned startup preloading. */
  startupReporting?: { generation: string }
}

/** Boot waits at most this long for the groups to preload. */
const PRELOAD_TIMEOUT_MS = 5_000

interface GroupModule {
  ensureComponent(componentId: string, options: { ComponentRegistry: ComponentRegistry, globals: object, packageLoader: PackageLoader }): void
}

interface Registration {
  type: string
  id: string
  v: string
  name: string
  packages: Types.Reference[]
  /** A layout's own stylesheet; the others' are in the group's. */
  stylesheet?: string | null
  load(this: Registration, context: { document: Types.Document, createControllerAeppic: (baseAeppic: any, queryRoot?: any) => any }): Promise<any>
}

interface ComponentRegistry {
  has(componentId: string): boolean
  register(componentId: string, registration: Registration): void
}

export type PackageLoader = (references: Types.Reference[]) => Promise<{ modules: object, components: object }>

export interface ViewComponentGroupsContext {
  options: ViewComponentsFeatureOptions
  packageLoader: PackageLoader
  /** v3's error rendering of dynamic components. */
  renderError: (createElement: any, error: any) => any
  /** Injected in tests; `import()` of the module URL otherwise. */
  importModule?: (url: string) => Promise<GroupModule>
  /**
   * Whether components bring their own stylesheets. As for components built
   * here (DynamicComponent): not with the unified stylesheet, nor when
   * stylesheets are disabled.
   */
  stylesheets?: boolean
  loadStyle?: (url: string, id: string) => Promise<unknown> | unknown
  /** Injected in tests; `loadStyle(css, id)` otherwise. */
  loadComponentStyle?: (css: string, id: string) => Promise<unknown> | unknown
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
}

export class ViewComponentGroups {
  private readonly _groups = new Map<string, Promise<GroupModule | null>>()
  private readonly _registrations = new Map<string, Registration>()
  private readonly _used: string[] = []
  private readonly _startupGroups = new Set<string>()
  private _startupFinished = false
  private readonly _registry: ComponentRegistry

  constructor(private readonly context: ViewComponentGroupsContext) {
    this._registry = {
      has: componentId => this._registrations.has(componentId) || Boolean(Vue.component(componentId)),
      register: (componentId, registration) => {
        this._registrations.set(componentId, registration)
      },
    }
    // Every page load records afresh.
    this._persistUsed()
  }

  /** The rules the server built the groups with. */
  get rules(): GroupRule[] {
    return this.context.options.groups ?? []
  }

  /**
   * Registers a component from its precompiled group. False: build it in the
   * browser (no group, not in the group, the group did not load).
   */
  async ensure(componentId: string, document: Types.Document): Promise<boolean> {
    const group = componentGroup(document as any, this.rules)

    this._noteStartupGroup(group?.id)

    if (Vue.component(componentId)) {
      return true
    }

    if (!group) {
      return false
    }

    const module = await this.loadGroup(group.id)

    if (!module) {
      return false
    }

    module.ensureComponent(componentId, { ComponentRegistry: this._registry, globals: { FieldParams }, packageLoader: this.context.packageLoader })
    const registration = this._registrations.get(componentId)

    if (!registration) {
      return false
    }

    if (registration.stylesheet && this.context.stylesheets !== false) {
      this._addStylesheet(registration)
    }

    const component = await perf.track('component:precompiled', () => registration.load({
      document,
      createControllerAeppic: controllerAeppicFactory(registration, document),
    }), String(document?.data?.name || ''))

    component.renderError = this.context.renderError
    Vue.component(componentId, component)
    return true
  }

  /**
   * Adds a layout's own stylesheet, under the document's id as
   * DynamicComponent does: the same layout built here replaces it instead of
   * adding a second one. A stylesheet that can't be added costs only that
   * layout its styles: the failure is logged, and the layout is shown
   * regardless.
   */
  private _addStylesheet(registration: Registration) {
    const loadComponentStyle = this.context.loadComponentStyle ?? ((css, id) => loadStyle(css, id))
    const failed = (error: unknown) => console.error(`[view-components] the stylesheet of ${registration.name} (${registration.id}@${registration.v}) was not added; the component is shown without it. The error below says why.`, error)

    try {
      Promise.resolve(loadComponentStyle(registration.stylesheet, registration.id)).catch(failed)
    } catch (error) {
      failed(error)
    }
  }

  /** Loads a group's module and stylesheet once per page; null if it cannot. */
  loadGroup(groupId: string): Promise<GroupModule | null> {
    let loading = this._groups.get(groupId)

    if (!loading) {
      loading = this._loadGroup(groupId)
      this._groups.set(groupId, loading)
    }

    return loading
  }

  /**
   * Loads the groups to preload, during boot, before the first layout
   * renders. Boot waits at most `timeoutMs`; groups still loading then finish
   * in the background.
   */
  async preload({ timeoutMs = PRELOAD_TIMEOUT_MS }: { timeoutMs?: number } = {}): Promise<void> {
    const loads = (this.context.options.preload ?? []).map(groupId => this.loadGroup(groupId))
    let timer

    const timedOut = await Promise.race([
      Promise.all(loads).then(() => false),
      new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(true), timeoutMs) }),
    ])
    clearTimeout(timer)

    if (timedOut) {
      console.warn(`[view-components] preloading took longer than ${timeoutMs} ms; boot continues`)
    }
  }

  /** Rendering demand before the dynamic renderer's registration cache check. */
  noteDemand(document: Types.Document): void {
    if (!this.context.options.startupReporting || this._startupFinished) {
      return
    }
    this._noteStartupGroup(componentGroup(document as any, this.rules)?.id)
  }

  get startupGeneration(): string | undefined {
    return this.context.options.startupReporting?.generation
  }

  private _noteStartupGroup(groupId: string | undefined): void {
    if (!this.context.options.startupReporting || this._startupFinished || !groupId) {
      return
    }
    if (this.context.options.groupKeys?.[groupId] && this._startupGroups.size < 256) {
      this._startupGroups.add(groupId)
    }
  }

  /** Close the initial-render observation window; report once, including empty starts. */
  finishStartup(): string[] | null {
    if (!this.context.options.startupReporting || this._startupFinished) {
      return null
    }
    this._startupFinished = true
    return [...this._startupGroups]
  }

  /** The groups this page load used, in order of first use. */
  usedGroups(): string[] {
    return [...this._used]
  }

  /** The used groups as JSON for `features.aeppic["view-components"].options.preload`. */
  preloadList(): string {
    return JSON.stringify(this._used, null, 2)
  }

  private async _loadGroup(groupId: string): Promise<GroupModule | null> {
    // The server's key: a group it does not know is built here.
    const key = this.context.options.groupKeys?.[groupId]

    if (!key) {
      return null
    }

    try {
      const urls = groupUrls(groupId, key)
      const importModule = this.context.importModule ?? (url => import(/* @vite-ignore */ url))
      const loadStyle = this.context.loadStyle ?? ((url, id) => loadStyleFromUrl(url, id))
      const [module] = await perf.track('component:group', () => Promise.all([
        importModule(urls.js),
        this.context.stylesheets === false ? null : loadStyle(urls.css, `ae-view-components:${groupId}`),
      ]), groupId)

      this._noteUse(groupId)
      return module
    } catch (error) {
      console.warn(`[view-components] group ${groupId} did not load; its components are built here`, error)
      return null
    }
  }

  private _noteUse(groupId: string) {
    if (!this._used.includes(groupId)) {
      this._used.push(groupId)
      this._persistUsed()
    }
  }

  private _persistUsed() {
    try {
      const storage = this.context.storage === undefined ? globalThis.localStorage : this.context.storage
      storage?.setItem(USED_GROUPS_STORAGE_KEY, JSON.stringify(this._used))
    } catch {
      // Storage may be unavailable (private mode): the list is in memory.
    }
  }
}

/** v3's controller Aeppic of a component (DynamicComponentBuilder). */
function controllerAeppicFactory(registration: Registration, document: Types.Document) {
  const contextName = `ae-${registration.type}s/controllers/${document.id}:${registration.name}`

  return (baseAeppic, queryRoot?) => {
    if (!baseAeppic || typeof baseAeppic.contextify !== 'function') {
      return baseAeppic
    }

    const controllerAeppic = baseAeppic.contextify(contextName, queryRoot)
    const baseRootElement = baseAeppic.context?.rootElement

    if (baseRootElement && controllerAeppic?.context) {
      controllerAeppic.context.rootElement = baseRootElement
    }

    return controllerAeppic
  }
}
